import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/useApi";
import type { UserListEntry } from "../lib/types";
import { formatDate } from "../lib/format";
import { Badge, Button, EmptyState, ErrorState, Input, PageHeader, Spinner, Table, Td } from "../components/ui";

const PAGE_SIZE = 50;

const PLATFORM_TONE = { auth: "blue", free_tools: "green", paid: "purple" } as const;

export default function Users() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useApi<{ total: number; users: UserListEntry[]; azureError?: string }>(
    `/users?search=${encodeURIComponent(query)}&limit=${PAGE_SIZE}&offset=${offset}`,
  );

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Unified view across the auth worker, free tools, and paid platform — open a user for full details"
      />

      {data?.azureError && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Paid platform unavailable — list may be missing Azure users ({data.azureError})
        </p>
      )}

      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setOffset(0);
          setQuery(search);
        }}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or name…"
          className="max-w-sm"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {loading ? (
        <Spinner />
      ) : error || !data ? (
        <ErrorState message={error ?? "Failed to load"} onRetry={reload} />
      ) : data.users.length === 0 ? (
        <EmptyState message="No users match." />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <Table headers={["Email", "Name", "Platforms", "Tier", "Sessions", "Auth account created"]}>
            {data.users.map((u) => (
              <tr key={u.email} className="hover:bg-slate-50">
                <Td>
                  <Link to={`/users/${encodeURIComponent(u.email)}`} className="font-medium text-rail-600 hover:underline">
                    {u.email}
                  </Link>
                </Td>
                <Td>{u.name ?? "—"}</Td>
                <Td>
                  <div className="flex gap-1">
                    {u.platforms.map((p) => (
                      <Badge key={p} tone={PLATFORM_TONE[p as keyof typeof PLATFORM_TONE] ?? "slate"}>
                        {p.replace("_", " ")}
                      </Badge>
                    ))}
                  </div>
                </Td>
                <Td>{u.tier ?? "—"}</Td>
                <Td>{u.activeSessions}</Td>
                <Td className="text-xs">{formatDate(u.authCreatedAt)}</Td>
              </tr>
            ))}
          </Table>
          <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
